import { IonToast } from "@ionic/react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Alert, IconButton, Snackbar, Tooltip } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import toastManager from "../../utils/toastManager";
import { usePlatformUI } from "../platform";

/**
 * Copy text to clipboard with fallback for older browsers
 */
const copyToClipboard = (text) => {
  try {
    navigator.clipboard.writeText(text);
  } catch (e) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
};

/**
 * Global Toast Container Component
 * Should be added once at the app root level
 * Uses Ionic on iOS/Android, MUI Snackbar on web
 */
const ToastContainer = () => {
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState(false);
  const { useIonic } = usePlatformUI();

  useEffect(() => {
    const unsubscribe = toastManager.subscribe((options) => {
      setCopied(false);
      setToast(options);
    });

    return unsubscribe;
  }, []);

  const handleCopy = useCallback(() => {
    if (toast?.message) {
      copyToClipboard(toast.message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, [toast?.message]);

  if (useIonic) {
    const buttons = [];

    if (toast?.copyable && toast?.message) {
      buttons.push({
        text: copied ? "Copied!" : "Copy",
        side: "end",
        handler: () => {
          copyToClipboard(toast.message);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
          return false; // Don't dismiss
        }
      });
    }

    return (
      <IonToast
        isOpen={toast !== null}
        onDidDismiss={() => setToast(null)}
        message={toast?.message}
        duration={toast?.duration}
        color={toast?.color}
        position={toast?.position}
        icon={toast?.icon}
        buttons={buttons}
        cssClass="custom-toast"
      />
    );
  }

  // MUI Snackbar for web
  const severityMap = { success: "success", error: "error", warning: "warning", danger: "error", primary: "info" };
  const severity = severityMap[toast?.color] || "info";
  const vertical = toast?.position?.includes("bottom") ? "bottom" : "top";
  const horizontal = toast?.position?.includes("start") ? "left" : toast?.position?.includes("end") ? "right" : "center";

  return (
    <Snackbar
      open={toast !== null}
      autoHideDuration={toast?.duration ?? 3000}
      onClose={() => setToast(null)}
      anchorOrigin={{ vertical, horizontal }}
    >
      <Alert
        onClose={() => setToast(null)}
        severity={severity}
        variant="filled"
        sx={{ width: "100%", alignItems: "center" }}
        action={
          toast?.copyable && toast?.message ? (
            <Tooltip title={copied ? "Copied!" : "Copy message"}>
              <IconButton
                size="small"
                color="inherit"
                onClick={handleCopy}
                sx={{ ml: 1 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null
        }
      >
        {toast?.message}
      </Alert>
    </Snackbar>
  );
};

export default ToastContainer;
